// =============================================================================
// api/rooms.ts
// =============================================================================

import type {
  MapPosition2D,
  Media,
  ReorderItem,
  RoomMeasures,
  UpdateRoomPayload,
  VRoom,
} from "@/types";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";
import { deleteMedia } from "./media";

//  ---------------
// FETCH
//  ---------------
export const fetchRoomView = async (roomId: string): Promise<VRoom | null> => {
  const { data, error } = await supabase
    .from("v_rooms")
    .select("*")
    .eq("id", roomId)
    .single();
  if (error) return null;
  return data as VRoom;
};

export const fetchRoomsByTour = async (tourId: string): Promise<VRoom[]> => {
  const { data, error } = await supabase
    .from("v_rooms")
    .select("*")
    .eq("tour_id", tourId)
    .order("sort_order", { ascending: true });

  if (error) handleSupabaseError(error, "fetchRoomsByTour");
  return (data ?? []) as VRoom[];
};

//  ---------------
// CREATE
//  ---------------
export const createRoom = async ({
  floor_id,
  tour_id,
  name,
  sort_order = 0,
  mts2,
}: {
  floor_id?: string | null;
  tour_id: string;
  name: string;
  sort_order?: number;
  mts2?: number | null;
}): Promise<VRoom> => {
  if (!tour_id)
    throw new Error("tour_id is strictly required for RLS policies");

  const anchorKey = floor_id ? "floor_id" : "tour_id";
  const anchorVal = floor_id ?? tour_id;

  const { count } = await supabase
    .from("rooms")
    .select("*", { count: "exact", head: true })
    .eq(anchorKey, anchorVal);

  const { data, error } = await supabase
    .from("rooms")
    .insert([
      {
        floor_id: floor_id ?? null,
        tour_id: tour_id,
        name,
        sort_order,
        is_main: count === 0,
        mts2: mts2 ?? null,
      },
    ])
    .select("*")
    .single();

  if (error) handleSupabaseError(error, "createRoom");
  return data as VRoom;
};

//  ---------------
// UPDATE
//  ---------------
export const updateRoom = async (
  id: string,
  updates: UpdateRoomPayload,
): Promise<void> => {
  const { error } = await supabase.from("rooms").update(updates).eq("id", id);
  if (error) handleSupabaseError(error, "updateRoom");
};

export const updateRoomMapPos = (id: string, map_pos: MapPosition2D) =>
  updateRoom(id, { map_pos });

export const updateRoomShape = (id: string, shape: MapPosition2D[] | null) =>
  updateRoom(id, { shape });

/** Persist the plan-save bundle: polygon + declared sides + height + derived
 *  area, in one UPDATE. `undefined` fields are omitted (left untouched). */
export const updateRoomMeasures = (
  id: string,
  m: { shape?: MapPosition2D[] | null } & RoomMeasures,
) => updateRoom(id, m);

export const updateRoomImg = (id: string, img_id: string | null) =>
  updateRoom(id, { img_id });

//  ---------------
// DELETE
//  ---------------
export const deleteRoom = async (id: string): Promise<void> => {
  // 1. Buscamos qué media tiene asignada la habitación antes de borrarla
  const { data: room } = await supabase
    .from("rooms")
    .select("img_id, audio_id")
    .eq("id", id)
    .single();

  // 2. Borramos la habitación (la DB elimina el registro en cascada)
  const { error } = await supabase.from("rooms").delete().eq("id", id);
  if (error) handleSupabaseError(error, "deleteRoom");

  // 3. Limpiamos los archivos físicos del Storage
  if (room) {
    if (room.img_id) {
      const { data: m } = await supabase
        .from("media")
        .select("*")
        .eq("id", room.img_id)
        .single();
      if (m) await deleteMedia(m as Media);
    }
    if (room.audio_id) {
      const { data: m } = await supabase
        .from("media")
        .select("*")
        .eq("id", room.audio_id)
        .single();
      if (m) await deleteMedia(m as Media);
    }
  }
};

//  ---------------
// SET MAIN
//  ---------------
export const setMainRoom = async (roomId: string): Promise<void> => {
  const { error } = await supabase.rpc("set_main_room", {
    target_room_id: roomId,
  });
  if (error) handleSupabaseError(error, "setMainRoom");
};

//  ---------------
// REORDER
//  ---------------
export const reorderRooms = async (rooms: ReorderItem[]): Promise<void> => {
  const { error } = await supabase.rpc("update_room_orders", {
    payload: rooms,
  });
  if (error) handleSupabaseError(error, "reorderRooms");
};
